<template>
  <div>
    <div class="d-flex justify-content-end">
      <b-button variant="primary" @click="openAddModal" class="my-1">
        Add Expense
      </b-button>
    </div>
    <b-modal
      id="addModal"
      :title="isEditMode ? 'Edit Expense' : 'Add Expense'"
      v-model="showAddModal"
      centered
      hide-footer
    >
      <b-row class="justify-content-center">
        <b-form @submit.prevent="AddExpense" class="p-3">
          <b-row>
            <b-col md="12">
              <b-form-group
                label="Expense Date"
                label-for="expenseDate"
                class="mb-4"
              >
                <b-form-datepicker
                  id="expenseDate"
                  v-model="newExpense.expenseDate"
                  required
                  class="form-control-custom"
                ></b-form-datepicker>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Tax" label-for="tax" class="mb-4">
                <b-form-select
                  id="tax"
                  v-model="newExpense.tax"
                  :options="taxOptions"
                  required
                  class="form-control-custom"
                >
                  <option :value="null" disabled>Select a tax</option>
                </b-form-select>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Total" label-for="total" class="mb-4">
                <b-form-input
                  id="total"
                  v-model.number="newExpense.total"
                  type="number"
                  step="0.01"
                  required
                  class="form-control-custom"
                  @input="calculateTaxAmount"
                >
                </b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="6">
              <b-form-group
                label="Reference"
                label-for="reference"
                class="mb-4"
              >
                <b-form-input
                  id="reference"
                  v-model="newExpense.reference"
                  required
                  class="form-control-custom"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                label="Tax Amount"
                label-for="taxAmount"
                class="mb-4"
              >
                <b-form-input
                  id="taxAmount"
                  v-model="newExpense.taxAmount"
                  readonly
                  type="number"
                  step="0.01"
                  class="form-control-custom"
                >
                </b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="12">
              <b-form-group label="Contact" label-for="contact" class="mb-4">
                <validation-provider
                  rules="required"
                  v-slot="{ errors }"
                  name="Contact"
                  vid="Contact"
                >
                  <b-form-select
                    id="contact"
                    v-model="newExpense.contact"
                    :options="contactOptions"
                    class="form-control-custom"
                  >
                    <option :value="null" disabled>Select a contact</option>
                  </b-form-select>
                  <small v-if="errors.length" class="text-danger">{{
                    errors[0]
                  }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>
          <div class="modal-footer d-flex justify-content-center">
            <button type="submit" class="btn btn-primary w-50">
              <b-spinner small v-if="loading" class="mx-2" />
              {{ isEditMode ? "Update" : "Add" }}
            </button>
          </div>
        </b-form>
      </b-row>
    </b-modal>

    <b-modal
      id="viewModal"
      title="View Expense"
      v-model="showViewModal"
      centered
    >
      <p><strong>Expense Date: </strong>{{ selectedExpense.expenseDate }}</p>
      <p><strong>Reference: </strong>{{ selectedExpense.reference }}</p>
      <p><strong>Total Amount: </strong>{{ selectedExpense.total }}</p>
      <p><strong>Currency: </strong>{{ selectedExpense.currency }}</p>
      <p><strong>Tax Amount: </strong>{{ selectedExpense.taxAmount }}</p>
    </b-modal>
    <h3 v-if="expenseList.length === 0" class="text-center">No Expense Data</h3>
    <b-table
      v-if="dataLoaded"
      :items="expenseList"
      :fields="fields"
      :current-page="currentPage"
      :per-page="perPage"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      responsive
      striped
    >
      <template #cell(expenseDate)="row">
        {{ row.item.expenseDate.split("T")[0] }}
      </template>
      <template #cell(contact)="row">
        {{ row.item.contactName }}
      </template>
      <template #cell(actions)="row">
        <b-dropdown variant="link" toggle-class="text-decoration-none" no-caret>
          <template #button-content>
            <feather-icon
              icon="MoreVerticalIcon"
              size="16"
              class="text-body align-middle mr-25"
            />
          </template>
          <b-dropdown-item @click="openViewModal(row.item)">
            <feather-icon icon="EyeIcon" class="mr-50" />
            <span>View</span>
          </b-dropdown-item>
          <b-dropdown-item v-b-modal.editModal @click="openEditModal(row.item)">
            <feather-icon icon="EditIcon" class="mr-50" />
            <span>Edit</span>
          </b-dropdown-item>
          <b-dropdown-item @click="confirmDelete(row.item)">
            <feather-icon icon="TrashIcon" class="mr-50" />
            <span>Delete</span>
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </b-table>

    <div v-else class="text-center p-4">
      <b-spinner variant="primary" label="Loading income data..."></b-spinner>
      <p>Loading data, please wait...</p>
    </div>

    <b-col cols="12">
      <b-pagination
        v-model="currentPage"
        :total-rows="totalRows"
        :per-page="perPage"
        align="center"
        size="sm"
        class="my-0"
      />
    </b-col>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from "vee-validate";
import {
  BFormInput,
  BFormGroup,
  BTable,
  BPagination,
  BRow,
  BCol,
  BButton,
  BInputGroup,
  BInputGroupAppend,
  BInputGroupPrepend,
  BFormTextarea,
  VBTooltip,
  BFormSelect,
  BBadge,
  BModal,
  VBModal,
  BDropdown,
  BDropdownItem,
  BFormCheckbox,
  BSpinner,
  BForm,
  BFormDatepicker,
  BOverlay,
} from "bootstrap-vue";
import Swal from "sweetalert2";
import CreditCardIcon from "./CreditCardIcon.vue";
import ReconcileIcon from "./ReconcileIcon.vue";
import FundTransfersIcon from "./FundTransfersIcon.vue";
import AccStatementIcon from "./AccStatementIcon.vue";
import CheckTransactionIcon from "./CheckTransactionIcon.vue";
import BankStatementIcon from "./SalesReportIcon.vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import SearchBar from "@/components/search-component/SearchBar.vue";
import { mapActions, mapGetters } from "vuex";
export default {
  components: {
    BFormInput,
    BFormGroup,
    BTable,
    BPagination,
    BRow,
    BCol,
    BButton,
    BInputGroup,
    BInputGroupAppend,
    BInputGroupPrepend,
    BFormTextarea,
    BFormSelect,
    BBadge,
    BModal,
    CreditCardIcon,
    BDropdown,
    BDropdownItem,
    vSelect,
    flatPickr,
    BFormCheckbox,
    SearchBar,
    ReconcileIcon,
    BankStatementIcon,
    CheckTransactionIcon,
    FundTransfersIcon,
    AccStatementIcon,
    BSpinner,
    BForm,
    BFormDatepicker,
    BOverlay,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    "b-tooltip": VBTooltip,
    "b-modal": VBModal,
  },
  data() {
    return {
      dataLoaded: false,
      selectedExpense: {
        expenseDate: "",
        reference: "",
        total: 0,
        currency: "",
        taxAmount: 0,
      },
      isEditMode: false,
      newExpense: {
        id: null,
        expenseDate: "",
        tax: null,
        total: 0,
        reference: "",
        taxAmount: 0,
        contact: null,
      },
      perPage: 5,
      loading: false,
      bankId: this.$route.params.id,
      totalRows: 1,
      bankName: "",
      name: "",
      description: "",
      currency: "",
      currentBalance: "",
      isActive: "",
      isExistingAccount: false,
      showAddModal: false,
      showEditModal: false,
      showAddCCModal: false,
      showViewModal: false,
      // newExpense: {
      //   expenseDate: "",
      //   tax: null,
      //   total: 0,
      //   reference: "",
      //   taxAmount: 0,
      // },
      taxOptions: [],
      contactOptions: [],
      items: [],
      searchTerm: "",
      sortBy: "bankName",
      sortDesc: false,
      currentPage: 1,
      expenseList: [],
      fields: [
        { key: "expenseDate", label: "Expense Date", sortable: true },
        { key: "reference", label: "Reference", sortable: true },
        { key: "total", label: "Total Amount", sortable: true },
        { key: "taxAmount", label: "Tax Amount", sortable: true },
        { key: "contact", label: "Contact", sortable: true },
        { key: "actions", label: "Actions" },
      ],
    };
  },
  methods: {
    async fetchContacts() {
      try {
        const response = await this.getCustomersListAction();
        if (response && response.result && response.result.length > 0) {
          console.log("Fetched contacts:", response.result);

          this.contactOptions = response.result.map((contact) => ({
            value: contact.id,
            text: `${contact.otherName} ${contact.surname || ''}`,
          }));
        } else {
          console.warn("No contacts found in API response.");
          this.contactOptions = [];
        }
      } catch (error) {
        console.error("Error fetching contacts:", error);
      }
    },
    openAddModal() {
      this.newExpense = {
        id: null,
        expenseDate: "",
        tax: null,
        total: 0,
        reference: "",
        taxAmount: 0,
      };
      this.isEditMode = false;
      this.showAddModal = true;
    },
    async confirmDelete(item) {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "Cancel",
        customClass: {
          confirmButton: "btn btn-danger swal-btn-spacing",
          cancelButton: "btn btn-secondary swal-btn-spacing",
        },
        buttonsStyling: false,
      });

      if (result.isConfirmed) {
        this.deleteExpense(item.id);
      }
    },
    async deleteExpense(id) {
      try {
        this.loading = true;
        await this.deleteExpenseAction(id);

        Swal.fire({
          title: "Deleted!",
          text: "The expense has been deleted.",
          icon: "success",
          customClass: {
            confirmButton: "btn btn-primary",
          },
          buttonsStyling: false,
        });

        this.fetchExpenseList();
      } catch (error) {
        console.error("Error deleting expense:", error);
        Swal.fire({
          title: "Error!",
          text: "Failed to delete expense. Please try again.",
          icon: "error",
          customClass: {
            confirmButton: "btn btn-primary",
          },
          buttonsStyling: false,
        });
      } finally {
        this.loading = false;
      }
    },
    errorAdd() {
      this.$swal({
        title: "Error!",
        text: " Failed!",
        icon: "error",
        customClass: {
          confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
      });
    },
    // error
    errorEdit() {
      this.$swal({
        title: "Error!",
        text: " Failed!",
        icon: "error",
        customClass: {
          confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
      });
    },
    ...mapActions("taxModule", ["getTaxListAction"]),
    ...mapActions("bankModule", [
      "getBankListAction",
      "getBankIDAction",
      "addBankAction",
      "updateBankAction",
    ]),
    ...mapActions("expenseModule", [
      "getExpenseListAction",
      "postExpenseAction",
      "updateExpenseAction",
      "deleteExpenseAction",
    ]),
    ...mapActions("customerModule", [
      "getCustomersListAction",
      "getCustomersByIDAction",
    ]),
    contactSelected(contactId) {
      const selectedContact = this.contactOptions.find(
        (contact) => contact.value === contactId
      );
      if (selectedContact) {
        this.newExpense.contactDetails = selectedContact.text;
      }
    },
    async fetchExpenseList() {
      this.dataLoaded = false;
      this.loading = true;
      try {
        const response = await this.getExpenseListAction(this.bankId);
        if (response && response.length > 0) {
          this.expenseList = response.map((expense) => {
            // Find the matching contact name
            const customer = this.contactOptions.find(
              (contact) => contact.value === expense.customerID
            );
            return {
              ...expense,
              contactName: customer ? customer.text : "-", // Use '-' if no match is found
            };
          });
          this.totalRows = response.length;
        } else {
          console.warn("No expense data received", response);
          this.expenseList = [];
        }
      } catch (error) {
        console.error("Error fetching expense list:", error);
      } finally {
        this.dataLoaded = true;
        this.loading = false;
      }
    },

    async AddExpense() {
      const actionText = this.isEditMode
        ? "Updating Expense..."
        : "Adding Expense...";
      const swalLoading = this.$swal({
        title: actionText,
        text: "Please wait while we process your request.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          this.$swal.showLoading();
        },
      });

      this.loading = true;

      const expenseData = {
        expenseDate: this.newExpense.expenseDate,
        tax: this.newExpense.tax,
        total: parseFloat(this.newExpense.total) || 0,
        reference: this.newExpense.reference,
        taxAmount: this.newExpense.taxAmount,
        accountID: this.bankId,
        taxID	: this.newExpense.tax,
        taxType: "0",
        statusID: "0",
        refID: "0",
        currency: "MUR",
        customerID: this.newExpense.contact,
      };
      console.log("Expense Data:", expenseData);
      try {
        let response;
        if (this.isEditMode && this.newExpense.id) {
          response = await this.updateExpenseAction({
            id: this.newExpense.id,
            ...expenseData,
          });
        } else {
          response = await this.postExpenseAction(expenseData);
        }

        if (response && response.success) {
          this.$swal.fire({
            title: "Success",
            text: this.isEditMode
              ? "Expense updated successfully!"
              : "Expense added successfully!",
            icon: "success",
            customClass: {
              confirmButton: "btn btn-primary",
            },
            buttonsStyling: false,
          });

          this.showAddModal = false;
          await this.fetchExpenseList();
        } else {
          throw new Error("Unexpected response format");
        }
      } catch (error) {
        this.$swal.fire({
          title: "Error",
          text: "Failed to save expense. Please try again.",
          icon: "error",
          customClass: {
            confirmButton: "btn btn-primary",
          },
          buttonsStyling: false,
        });
        console.error("Error saving expense:", error);
      } finally {
        this.loading = false;
        this.$swal.close();
      }
    },

    openViewModal(expense) {
      this.selectedExpense = {
        expenseDate: expense.expenseDate
          ? expense.expenseDate.split("T")[0]
          : "",
        reference: expense.reference || "",
        total: expense.total || 0,
        currency: expense.currency || "MUR",
        taxAmount: expense.taxAmount || 0,
      };
      this.showViewModal = true;
    },
    openEditModal(expense) {
      console.log("expense", expense);
      this.newExpense = {
        id: expense.id,
        expenseDate: expense.expenseDate
          ? expense.expenseDate.split("T")[0]
          : "",
        tax: expense.taxID	 ? expense.taxID	 : null,
        taxID: expense.taxID	 ? expense.taxID	 : null,
        total: expense.total || 0,
        reference: expense.reference || "",
        taxAmount: expense.taxAmount || 0,
        contact: expense.customerID || null,
      };

      // Ensure tax dropdown is populated correctly
      if (expense.tax) {
        const selectedTax = this.taxOptions.find(
          (tax) => tax.value == expense.tax
        );
        if (selectedTax) {
          this.newExpense.tax = selectedTax.value;
        } else {
          this.newExpense.tax = null;
        }
      }

      this.calculateTaxAmount();

      this.isEditMode = true;
      this.showAddModal = true;
    },
    calculateTaxAmount() {
      if (this.newExpense.tax && this.newExpense.total) {
        const selectedTax = this.taxOptions.find(
          (tax) => tax.value == this.newExpense.tax
        );
        if (selectedTax) {
          this.newExpense.taxAmount =
            ((parseFloat(this.newExpense.total) || 0) * selectedTax.taxAmount) /
            100;
        } else {
          this.newExpense.taxAmount = 0;
        }
      } else {
        this.newExpense.taxAmount = 0;
      }
    },
  },
  computed: {
    contactNameMapping() {
      return this.contactOptions.reduce((acc, contact) => {
        acc[contact.value] = contact.text; // Ensure correct field names
        return acc;
      }, {});
    },
    ...mapGetters("bankModule", {
      banks: "bank",
      loading: "loading",
    }),
    calculatedTaxAmount() {
      const selectedTax = this.taxOptions.find(
        (tax) => tax.value === this.newExpense.tax
      );
      return selectedTax
        ? ((parseFloat(this.newExpense.total) || 0) * selectedTax.taxAmount) /
            100
        : 0;
    },
  },
  async mounted() {
    await this.fetchContacts();
    await this.fetchExpenseList();
    try {
      const response = await this.getTaxListAction();
      if (response && response.result && response.result.length > 0) {
        console.log("Tax API Response in Component:", response.result);
        this.taxOptions = response.result.map((tax) => ({
          value: tax.id,
          text: `${tax.name} (${tax.code}) - ${tax.taxAmount}%`,
          taxAmount: tax.taxAmount,
        }));
      } else {
        console.warn(
          "Tax API Response is empty or not in expected format",
          response
        );
      }
    } catch (error) {
      console.error("Component: Error fetching tax options:", error);
    }

    try {
    } catch (error) {
      console.error("Error fetching contacts:", error);
    }
  },
  watch: {
    "newExpense.tax": function () {
      this.calculateTaxAmount();
    },
    "newExpense.total": function () {
      this.calculateTaxAmount();
    },
    taxOptions: function (newVal) {
      if (this.isEditMode && this.newExpense.tax) {
        const selectedTax = newVal.find(
          (tax) => tax.value == this.newExpense.tax
        );
        if (selectedTax) {
          this.newExpense.tax = selectedTax.value;
          this.calculateTaxAmount();
        }
      }
    },
  },
};
</script>
<style>
div #viewModal footer {
  display: none !important;
}

.swal-btn-spacing {
  margin: 0 10px;
  /* Adjust spacing as needed */
}

.input-group:not(.bootstrap-touchspin):focus-within {
  box-shadow: none;
}
</style>
<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>